import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Sidebar from './Sidebar';
import Scanner from './Scanner';
import Demandes from './Demandes';
import DemandesEnCours from './DemandesEnCours';
import Historique from './pages/Historique';
import Clients from './pages/Clients'; 
import Devis from './pages/Devis';
import Factures from './pages/Factures';
import Parametres from './pages/Parametres';
import Planning from './pages/Planning';
import MenusPlanning from './pages/MenusPlanning';
import PretesALivrer from './pages/PretesALivrer';
import Services from './pages/Services';
import CalendarSettings from './pages/CalendarSettings';
import Abonnements from './pages/Abonnements';
import AdminAccountSettings from './pages/AdminAccountSettings';
import APreparer from './pages/APreparer';
import Consolidation from './pages/Consolidation';
import FeuilleRoute from './pages/FeuilleRoute';
import Validation from './pages/Validation';
import Statistiques from './pages/Statistiques';
import Depenses from './pages/Depenses';
import Plats from './pages/Plats';
import Events from './pages/Events';
import Accueil from './pages/Accueil';
import JournalAlimentaire from './pages/JournalAlimentaire';
import { useBusinessUnit } from './BusinessUnitContext';
import DeliveryTracker from './components/DeliveryTracker';

// --- Composants Login ---
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setMessage(error.message); setIsError(true); }
    setLoading(false);
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f9f9f9' }}>
      <div style={{ width: '400px', padding: '30px', background: 'white', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
        <h2 style={{ textAlign: 'center', color: '#333', marginBottom: '25px' }}>Connexion Administrateur</h2>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email:</label>   
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '20px', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Mot de passe:</label>
            <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '35px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#666',
              }}
            >
              {showPassword ? '👁️' : '🔒'}
            </button>
          </div>
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', backgroundColor: '#d4af37', color: 'white', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' }}>
            {loading ? 'Chargement...' : 'Se connecter'}
          </button>
          {message && <p style={{ textAlign: 'center', marginTop: '15px', color: isError ? 'red' : 'green' }}>{message}</p>}
        </form>
      </div>
    </div>
  );
};

const mainContentStyle = { flex: 1, padding: '20px', overflowY: 'auto', backgroundColor: '#f4f7fa' };     

const DashboardLayout = () => {
    const { businessUnit } = useBusinessUnit();
    const [newCount, setNewCount] = useState(0);
    const [inProgressCount, setInProgressCount] = useState(0);
    const [pendingQuotesCount, setPendingQuotesCount] = useState(0);
    const [draftQuotesCount, setDraftQuotesCount] = useState(0);
    const [toPrepareCount, setToPrepareCount] = useState(0);
    const [readyCount, setReadyCount] = useState(0);
    const [pendingInvoicesCount, setPendingInvoicesCount] = useState(0);
    const [depositPaidInvoicesCount, setDepositPaidInvoicesCount] = useState(0);
    const [waitingForPrepCount, setWaitingForPrepCount] = useState(0);
    const [activeSubscriptionsCount, setActiveSubscriptionsCount] = useState(0);
    const [subscriptionsNeedAttentionCount, setSubscriptionsNeedAttentionCount] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [notifications, setNotifications] = useState([]);
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    
    // --- ANTI-DOUBLON NOTIFICATIONS ---
    const lastNotifRef = useRef({ msg: '', time: 0 });

    useEffect(() => {
        const unlock = () => {
            if (!audioUnlocked) {
                const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
                audio.volume = 0; 
                audio.play().then(() => {
                    setAudioUnlocked(true);
                    window.removeEventListener('click', unlock);
                }).catch(() => {});
            }
        };
        window.addEventListener('click', unlock);
        return () => window.removeEventListener('click', unlock);
    }, [audioUnlocked]);

    const fetchCounts = useCallback(async () => {
        // FIXED: Count Intention WhatsApp + Nouvelle + En attente de traitement for "Nouvelles"
        const { count: newDemandsCount } = await supabase
            .from('demandes')
            .select('*', { count: 'exact', head: true })
            .in('status', ['Nouvelle', 'Intention WhatsApp', 'En attente de traitement'])
            .eq('business_unit', businessUnit);
        setNewCount(newDemandsCount || 0);

        // FIXED: Count only confirmed/paying for "En cours" (exclude draft/awaiting review/sent quotes)
        const { count: inProgressDemandsCount } = await supabase
            .from('demandes')
            .select('*', { count: 'exact', head: true })
            .eq('business_unit', businessUnit)
            .or(`and(type.in.("COMMANDE_MENU","COMMANDE_SPECIALE"),status.in.("confirmed","En attente de paiement","En cours de traitement")),and(type.eq.RESERVATION_SERVICE,status.in.("confirmed","En attente de paiement","En cours de traitement"))`);
        setInProgressCount(inProgressDemandsCount || 0);

        const { count: sentQuotesCount } = await supabase.from('quotes').select('*', { count: 'exact', head: true }).eq('status', 'sent').eq('business_unit', businessUnit);
        setPendingQuotesCount(sentQuotesCount || 0);

        const { count: draftQuotes } = await supabase.from('quotes').select('*', { count: 'exact', head: true }).eq('status', 'draft').eq('business_unit', businessUnit);
        setDraftQuotesCount(draftQuotes || 0);

        const { count: toPrepareDemandsCount } = await supabase.from('demandes').select('*', { count: 'exact', head: true }).in('status', ['En attente de préparation', 'Préparation en cours']).eq('business_unit', businessUnit);
        setToPrepareCount(toPrepareDemandsCount || 0);

        const { count: readyDemandsCount } = await supabase.from('demandes').select('*', { count: 'exact', head: true }).eq('status', 'Prêt pour livraison').eq('business_unit', businessUnit);
        setReadyCount(readyDemandsCount || 0);

        const { count: pendingInvoices } = await supabase.from('invoices').select('*', { count: 'exact', head: true }).eq('status', 'pending').eq('business_unit', businessUnit);
        setPendingInvoicesCount(pendingInvoices || 0);

        const { count: depositPaidInvoices } = await supabase.from('invoices').select('*', { count: 'exact', head: true }).eq('status', 'deposit_paid').eq('business_unit', businessUnit);
        setDepositPaidInvoicesCount(depositPaidInvoices || 0);

        const { count: waitingForPrep } = await supabase.from('invoices').select('*, demandes!inner(status)', { count: 'exact', head: true }).eq('status', 'paid').not('demandes.status', 'in', '("En attente de préparation","Préparation en cours","completed")').eq('business_unit', businessUnit);
        setWaitingForPrepCount(waitingForPrep || 0);

        const { count: activeSubsCount } = await supabase.from('abonnements').select('*', { count: 'exact', head: true }).eq('status', 'actif').eq('business_unit', businessUnit);
        setActiveSubscriptionsCount(activeSubsCount || 0);

        const { data: needsAttentionSubs } = await supabase.from('abonnements').select('id, monthly_price').eq('status', 'actif').eq('business_unit', businessUnit);
        const needsAttentionCount = needsAttentionSubs?.filter(sub => !sub.monthly_price || sub.monthly_price <= 0).length || 0;
        setSubscriptionsNeedAttentionCount(needsAttentionCount);
    }, [businessUnit]);

    useEffect(() => {
        fetchCounts();

                const handleDbChanges = (payload) => {
                    if (payload.new && payload.new.business_unit && payload.new.business_unit !== businessUnit) return;
        
                    let newNotification = null;
                    const now = Date.now();
        
                    // Vérifier si c'est un doublon (même message en moins de 2s)
                    const isDuplicate = (msg) => {
                        if (lastNotifRef.current.msg === msg && now - lastNotifRef.current.time < 2000) return true;
                        return false;
                    };
        
                    if (payload.table === 'demandes') {
                        if (payload.eventType === 'INSERT') {
                            const msg = `Nouvelle demande reçue (${payload.new.type}).`;
                            if (!isDuplicate(msg)) newNotification = { id: now, type: 'info', message: msg, timestamp: new Date(), link: '/nouvelles-demandes' };
                        }
                        else if (payload.eventType === 'UPDATE' && payload.new.status === 'En attente de préparation' && payload.old.status !== 'En attente de préparation') {
                            const msg = `💰 Commande Menu payée ! À préparer.`;
                            if (!isDuplicate(msg)) newNotification = { id: now, type: 'success', message: msg, timestamp: new Date(), link: '/a-preparer' };
                        }
                        else if (payload.eventType === 'UPDATE' && payload.new.status === 'Confirmée par client' && payload.old.status !== 'Confirmée par client') {
                            const msg = `Un client a confirmé son intérêt pour une prestation !`;
                            if (!isDuplicate(msg)) newNotification = { id: now, type: 'success', message: msg, timestamp: new Date(), link: '/demandes-en-cours' };
                        }
                    }
                    else if (payload.table === 'quotes' && payload.eventType === 'UPDATE') {
                        if (payload.new.status === 'accepted' && payload.old.status !== 'accepted') {
                            const statusText = payload.new.signed_at ? "SIGNÉ" : "accepté";
                            const msg = `✅ Devis #${payload.new.document_number} ${statusText} !`;
                            if (!isDuplicate(msg)) newNotification = { id: now, type: 'success', message: msg, timestamp: new Date(), link: '/devis' };
                        } else if (payload.new.status === 'rejected' && payload.old.status !== 'rejected') {
                            const msg = `❌ Devis #${payload.new.document_number} refusé.`;
                            if (!isDuplicate(msg)) newNotification = { id: now, type: 'error', message: msg, timestamp: new Date(), link: '/devis' };
                        }
                    }
                    else if (payload.table === 'invoices' && payload.eventType === 'UPDATE') {
                         if (payload.new.status === 'paid' && payload.old.status !== 'paid') {
                            const msg = `💰 Facture #${payload.new.document_number} entièrement payée !`;
                            if (!isDuplicate(msg)) newNotification = { id: now, type: 'success', message: msg, timestamp: new Date(), link: '/factures' };
                        } else if (payload.new.status === 'deposit_paid' && payload.old.status !== 'deposit_paid') {
                            const msg = `💳 Acompte reçu pour la facture #${payload.new.document_number}.`;
                            if (!isDuplicate(msg)) newNotification = { id: now, type: 'info', message: msg, timestamp: new Date(), link: '/factures' };
                        }
                    }
        
                    if (newNotification) {
                        lastNotifRef.current = { msg: newNotification.message, time: now };
                        setNotifications(prev => [newNotification, ...prev]);                if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('Asiacuisine.re', { body: newNotification.message });
                }
                try {
                    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
                    const playPromise = audio.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(() => {});
                    }
                } catch (e) {}
            }
            fetchCounts();
        };

        const channel = supabase.channel('all-db-changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'demandes' }, handleDbChanges) 
            .on('postgres_changes', { event: '*', schema: 'public', table: 'quotes' }, handleDbChanges)   
            .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices' }, handleDbChanges) 
            .subscribe();

        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);

        return () => {
            supabase.removeChannel(channel);
            window.removeEventListener('resize', handleResize);
        };
    }, [fetchCounts, businessUnit]);

    return (
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100vh', overflow: 'hidden' }}>
            <Sidebar
                newCount={newCount} inProgressCount={inProgressCount} pendingQuotesCount={pendingQuotesCount}
                draftQuotesCount={draftQuotesCount}
                toPrepareCount={toPrepareCount} readyCount={readyCount} pendingInvoicesCount={pendingInvoicesCount}
                depositPaidInvoicesCount={depositPaidInvoicesCount} waitingForPrepCount={waitingForPrepCount}
                activeSubscriptionsCount={activeSubscriptionsCount} subscriptionsNeedAttentionCount={subscriptionsNeedAttentionCount}
                isMobile={isMobile} notifications={notifications} setNotifications={setNotifications}     
            />
            <main style={mainContentStyle}>
                <Routes>
                    <Route path="/" element={<Accueil />} />
                    <Route path="/planning" element={<Planning />} />
                    <Route path="/nouvelles-demandes" element={<Demandes />} />
                    <Route path="/demandes-en-cours" element={<DemandesEnCours />} />
                    <Route path="/a-preparer" element={<APreparer />} />
                    <Route path="/pretes" element={<PretesALivrer />} />
                    <Route path="/consolidation" element={<Consolidation />} />
                    <Route path="/feuille-route" element={<FeuilleRoute />} />
                    <Route path="/historique" element={<Historique />} />
                    <Route path="/particuliers" element={<Clients />} />
                    <Route path="/entreprises" element={<Clients />} />
                    <Route path="/devis" element={<Devis />} />
                    <Route path="/factures" element={<Factures />} />
                    <Route path="/scanner" element={<Scanner />} />
                    <Route path="/parametres" element={<Parametres />} />
                    <Route path="/menus-planning" element={<MenusPlanning />} />
                    <Route path="/validation" element={<Validation />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/calendrier" element={<CalendarSettings />} />
                    <Route path="/abonnements" element={<Abonnements />} />
                    <Route path="/admin-account" element={<AdminAccountSettings />} />
                    <Route path="/plats" element={<Plats />} />
                    <Route path="/statistiques" element={<Statistiques />} />
                    <Route path="/depenses" element={<Depenses />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/journal" element={<JournalAlimentaire />} />
                </Routes>
            </main>
            <DeliveryTracker />
        </div>
    );
};

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => { setSession(session); setLoading(false); });
    supabase.auth.onAuthStateChange((_event, session) => { setSession(session); });
  }, []);

  if (loading) return <div style={{ textAlign: 'center', marginTop: '50px' }}>Chargement...</div>;        

  return (
      <Routes>
        <Route path="/login" element={!session ? <Login /> : <Navigate to="/" />} />
        <Route path="/*" element={session ? <DashboardLayout /> : <Navigate to="/login" />} />
      </Routes>
  );
}

export default App;
